// React & Hooks
import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";

// Form Handling
import { useForm } from "react-hook-form";

// Internationalization
import { useTranslation } from "react-i18next";

// Context
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";

// MUI Components
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  Stack,
  Typography,
} from "@mui/material";

// MUI Icons
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VerifiedIcon from "@mui/icons-material/Verified";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// Services
import { httpGet, httpPost } from "../../services/commonService";
import { selectUserDetail } from "../../redux/slice/userSlice";

// Constants
import urlConstant from "../../constants/urlConstants";
import dataConstant from "../../constants/dataConstants";

// Shared Components
import CustomTextFieldComponent from "../../shared-components/form-controls/customTextField";
import CustomAutoCompleteDropdown from "../../shared-components/form-controls/customAutoCompleteDropdown";

// Snackbar
import SnackState from "../../shared-components/snackbar/snackState";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";

export default function EVMWallet() {
  const { t } = useTranslation();
  const getLoggedUserDetails = useSelector(selectUserDetail);
  const userAcctId = getLoggedUserDetails?.UserAccountDetails?.AcctID;
  const authenticationState = useContext(AuthenticationContext);
  const [snack, closeSnack, showSnackbar] = SnackState();
  const [dropdownData, setDropdownData] = useState({ walletType: [] });
  const [pageState, setPageState] = useState({
    isSaving: false,
    existingWallet: null,
    WalletStatus: null,
  });

  const methods = useForm({
    mode: "all",
    defaultValues: {
      walletAddress: "",
      walletType: "EVM",
    },
  });

  const { handleSubmit, setValue, watch } = methods;

  const walletAddressValue = watch("walletAddress");

  const sanitiseInput = (raw) =>
    raw
      .replace(/[\u200B-\u200D\uFEFF\u00AD]/g, "")
      .replace(/[\r\n]+/g, "")
      .trim();

  const validateWalletAddress = (rawValue) => {
    if (!rawValue) return t("PublicWalletAddress.Required");

    const value = sanitiseInput(rawValue);

    // Block if it looks like a seed phrase
    if (/\s/.test(value)) {
      return t("PublicWalletAddress.NoPrivateKeyOrSeed");
    }

    // Block strings longer than 42 characters that look like private keys
    if (
      value.length > 42 &&
      dataConstant.PublicWalletAddress.PRIVATE_KEY_REGEX.test(value)
    ) {
      return t("PublicWalletAddress.NoPrivateKeyOrSeed");
    }

    // Block any input longer than 42 characters
    if (value.length > 42) {
      return t("PublicWalletAddress.InvalidLength");
    }

    // Seed phrase word pattern fallback
    if (dataConstant.PublicWalletAddress.SEED_PHRASE_REGEX.test(value)) {
      return t("PublicWalletAddress.NoPrivateKeyOrSeed");
    }

    // Final EVM format check
    if (!dataConstant.PublicWalletAddress.EVM_ADDRESS_REGEX.test(value)) {
      return t("PublicWalletAddress.InvalidFormatEVM");
    }

    return true;
  };

  const liveValidation = (() => {
    if (!walletAddressValue) return null;
    const result = validateWalletAddress(walletAddressValue);
    return result === true ? "valid" : "invalid";
  })();

  useEffect(() => {
    if (!userAcctId) return;
    fetchExistingWalletAddress();
    getAllDropDownList();
  }, [userAcctId]);

  const getAllDropDownList = () => {
    const dropDownTypes = [
      dataConstant.AllDropDown.PublicWalletAddressDropDown,
    ];
    const model = {
      DropDownTypes: dropDownTypes.join(),
    };
    httpPost(
      urlConstant.Common.GetAllDropDownData,
      model,
      true,
      true,
      authenticationState.accessToken,
      authenticationState.impersonateEmail,
    )
      .then((response) => {
        const PublicWalletAddressList =
          response.Value.PublicWalletAddressDropDownResult.Data;

        setDropdownData((prev) => ({
          ...prev,
          walletType: PublicWalletAddressList,
        }));
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const fetchExistingWalletAddress = async () => {
    try {
      const response = await httpGet(
        `${urlConstant.PublicWalletAddress.GetPublicWalletAddress}?AcctId=${userAcctId}`,
        true,
        true,
        authenticationState.accessToken,
        authenticationState.impersonateEmail,
      );
      if (response?.Value?.Status === dataConstant.APIResponseStatus.Success) {
        const walletData = response.Value.Data;
        if (walletData) {
          setPageState((prev) => ({ ...prev, existingWallet: walletData }));
          setValue("walletAddress", walletData.WalletAddress ?? "");
          setValue("walletType", walletData.WalletType ?? "EVM");
        }
      }
    } catch (err) {
      console.error("Failed to fetch wallet address:", err);
    }
  };

  const onSubmit = async (data) => {
    const sanitised = sanitiseInput(data.walletAddress);

    const model = {
      AcctId: userAcctId,
      WalletAddress: sanitised,
      WalletType: data.walletType,
      WalletVisibility: "Public",
      WalletStatus: "NotVerified",
    };

    setPageState((prev) => ({ ...prev, isSaving: true }));
    try {
      const response = await httpPost(
        urlConstant.PublicWalletAddress.SavePublicWalletAddress,
        model,
        true,
        true,
        authenticationState.accessToken,
        authenticationState.impersonateEmail,
      );
      if (response?.Value?.Status === dataConstant.APIResponseStatus.Success) {
        const result = response.Value.Data;
        setPageState((prev) => ({
          ...prev,
          existingWallet: {
            ...prev.existingWallet,
            WalletAddress: result.WalletAddress,
            WalletType: data.walletType,
            WalletStatus: result.WalletStatus,
          },
        }));
        showSnackbar(response.Value.Message, "success");
        const verifyUrl = process.env.REACT_APP_VERIFY_LOCK_SLYCE_URL;
        if (verifyUrl) {
          window.open(verifyUrl, "_blank");
        }
      } else {
        showSnackbar(response?.Value?.Message, "error");
      }
    } catch (err) {
      showSnackbar("An unexpected error occurred. Please try again.", "error");
    } finally {
      setPageState((prev) => ({ ...prev, isSaving: false }));
    }
  };

  const chipStyles = {
    success: (theme) => ({
      background: "rgba(34, 197, 94, 0.12)",
      color: theme.palette.success.light ?? "#4ade80",
      border: "1px solid rgba(34,197,94,0.25)",
      borderRadius: "20px",
      fontSize: "0.72rem",
      fontWeight: 600,
      px: 0.5,
      "& .MuiChip-icon": {
        color: theme.palette.success.light ?? "#4ade80",
        fontSize: "0.9rem !important",
      },
    }),
    warning: (theme) => ({
      background: "rgba(245, 158, 11, 0.12)",
      color: theme.palette.warning.light ?? "#fbbf24",
      border: "1px solid rgba(245,158,11,0.25)",
      borderRadius: "20px",
      fontSize: "0.72rem",
      fontWeight: 600,
      px: 0.5,
      "& .MuiChip-icon": {
        color: theme.palette.warning.light ?? "#fbbf24",
        fontSize: "0.9rem !important",
      },
    }),
    info: (theme) => ({
      background: "rgba(99, 179, 237, 0.12)",
      color: theme.palette.info.light ?? "#63b3ed",
      border: "1px solid rgba(99,179,237,0.25)",
      borderRadius: "20px",
      fontSize: "0.72rem",
      fontWeight: 600,
      px: 0.5,
      "& .MuiChip-icon": {
        color: theme.palette.info.light ?? "#63b3ed",
        fontSize: "0.9rem !important",
      },
    }),
  };

  const renderStatusChip = () => {
    if (!pageState.existingWallet) {
      return (
        <Chip
          icon={<InfoOutlinedIcon />}
          label={t("PublicWalletAddress.StatusNoWalletLinked")}
          size="small"
          sx={chipStyles.info}
        />
      );
    }

    if (pageState.existingWallet.WalletStatus === "Verified") {
      return (
        <Chip
          icon={<VerifiedIcon />}
          label={t("PublicWalletAddress.StatusVerified")}
          size="small"
          sx={chipStyles.success}
        />
      );
    }

    return (
      <Chip
        icon={<WarningAmberIcon />}
        label={t("PublicWalletAddress.StatusNotVerified")}
        size="small"
        sx={chipStyles.warning}
      />
    );
  };

  const formConfig = {
    walletFieldConfig: {
      name: "walletAddress",
      label: "Public Wallet Address",
      placeholder: "0x...",
      type: "text",
      maxLength: 42,
      required: true,
      capitalize: false,
      isDisabled: !!pageState.existingWallet?.WalletAddress,
      validation: {
        validate: validateWalletAddress,
      },
      onCustomChange: (value) => {
        if (value.length > 42) {
          setValue("walletAddress", value.slice(0, 42));
        }
      },
      inputAdornment: (
        <AccountBalanceWalletOutlinedIcon
          sx={{ fontSize: "1.1rem", color: "primary.main" }}
        />
      ),
      endInputAdornment: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="caption"
            sx={(theme) => ({
              color: theme.palette.secondary.dark,
            })}
          >
            {`${walletAddressValue?.length ?? 0}/42`}
          </Typography>
        </Box>
      ),
    },

    walletTypeDropdown: {
      name: "walletType",
      label: "Wallet Type",
      type: "select",
      required: true,
      dropDownList: dropdownData.walletType,
      isDisabled: true,
    },
  };

  return (
    <>
      <Box flexDirection={"column"} gap={2} display={"flex"}>
        <Typography color="secondary.light" variant="h4" fontWeight={600}>
          {t("My Account")}
        </Typography>

        <Box width={"100%"}>
          <Card
            sx={(theme) => ({
              p: {
                xs: "20px",
                sm: "25px 30px",
              },
              background: theme.palette.background.cardGradient,
              borderRadius: "20px",
              height: "100%",
            })}
          >
            <Stack gap={"20px"}>
              <Stack direction={"column"} gap={0.5}>
                <Typography
                  color="secondary.light"
                  variant="h4"
                  fontWeight={600}
                >
                  {t("PublicWalletAddress.PublicWalletAddress")}
                </Typography>
                <Typography>
                  {t("PublicWalletAddress.WalletAddressNote")}
                </Typography>
              </Stack>
              <Alert
                icon={<InfoOutlinedIcon fontSize="inherit" />}
                severity="error"
                variant="outlined"
              >
                <Typography variant="body2" fontWeight={600} component="span">
                  {t("PublicWalletAddress.PrivateInformationWarning")}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.25 }}>
                  {t(
                    "PublicWalletAddress.PrivateInformationWarningSupportText",
                  )}
                </Typography>
              </Alert>

              <Card
                sx={(theme) => ({
                  p: { xs: "20px", sm: "24px 28px" },
                  background: theme.palette.primary.lightdark,
                  borderRadius: "16px",
                  width: "100%",
                })}
              >
                {pageState.existingWallet?.WalletAddress && (
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={1}
                    mb={2.5}
                  >
                    <Box>
                      <Typography
                        color="secondary.light"
                        variant="h6"
                        fontWeight={600}
                      >
                        {t("PublicWalletAddress.WalletInformation")}
                      </Typography>
                    </Box>
                    {renderStatusChip()}
                  </Stack>
                )}

                <form
                  onSubmit={handleSubmit(onSubmit)}
                  style={{ width: "100%" }}
                >
                  <Stack gap={"20px"}>
                    <Grid container spacing={"20px"}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <CustomTextFieldComponent
                          methods={methods}
                          properties={formConfig.walletFieldConfig}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {t("PublicWalletAddress.WalletAddressFormatNote")}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <CustomAutoCompleteDropdown
                          methods={methods}
                          properties={formConfig.walletTypeDropdown}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {t(
                            "PublicWalletAddress.WalletAddressCompatibilityNote",
                          )}
                        </Typography>
                      </Grid>
                      {!pageState.existingWallet?.WalletAddress && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 0.75, fontWeight: 500 }}
                          >
                            {t("PublicWalletAddress.ValidationStatus")}
                          </Typography>

                          {liveValidation === "valid" ? (
                            <Chip
                              icon={<CheckCircleOutlineIcon />}
                              label={t("PublicWalletAddress.FormatValidation")}
                              size="small"
                              sx={chipStyles.success}
                            />
                          ) : liveValidation === "invalid" ? (
                            <Chip
                              icon={<WarningAmberIcon />}
                              label={t("PublicWalletAddress.InvalidFormat")}
                              size="small"
                              sx={(theme) => ({
                                background: "rgba(248, 113, 113, 0.12)",
                                color: theme.palette.error.light ?? "#f87171",
                                border: "1px solid rgba(248,113,113,0.25)",
                                borderRadius: "20px",
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                px: 0.5,
                                "& .MuiChip-icon": {
                                  color: theme.palette.error.light ?? "#f87171",
                                  fontSize: "0.9rem !important",
                                },
                              })}
                            />
                          ) : (
                            <Chip
                              icon={<InfoOutlinedIcon />}
                              label={t("PublicWalletAddress.ValidationPending")}
                              size="small"
                              sx={chipStyles.info}
                            />
                          )}
                        </Grid>
                      )}
                    </Grid>

                    <Stack direction="row" justifyContent="flex-end" gap={2}>
                      {!pageState.existingWallet?.WalletAddress && (
                        <Button
                          id="save-wallet-address-btn"
                          type="submit"
                          variant="contained"
                          color="primary"
                          disabled={
                            pageState.isSaving ||
                            pageState.isResetting ||
                            !!pageState.existingWallet?.WalletAddress
                          }
                          startIcon={<AccountBalanceWalletOutlinedIcon />}
                          sx={{ px: 4, borderRadius: "25px" }}
                        >
                          {t("PublicWalletAddress.SaveWalletAddress")}
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </form>
              </Card>
            </Stack>
          </Card>
        </Box>
      </Box>
      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} />
    </>
  );
}
